import dayjs from 'dayjs';
import type { ColumnConfig } from '../types/panelConfig';
import type { DataGridColumn } from './yeni-design/DataGrid';

const currencyFormatter = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
const numberFormatter = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const percentFormatter = new Intl.NumberFormat('tr-TR', { style: 'percent', minimumFractionDigits: 2 });

// Backend metadata emits .NET/Excel tokens (yyyy, dd); dayjs expects YYYY/DD.
export function toDayjsFormat(dotnetFormat: string): string {
  return dotnetFormat.replace(/yyyy/g, 'YYYY').replace(/yy/g, 'YY').replace(/dd/g, 'DD').replace(/\bd\b/g, 'D');
}

export function formatValue(col: ColumnConfig, value: unknown): string {
  if (value == null) return '';
  if (col.cellRenderer === 'currency' || col.type === 'currency') return currencyFormatter.format(Number(value));
  if (col.cellRenderer === 'percentage' || col.type === 'percentage') return percentFormatter.format(Number(value) / 100);
  if (col.type === 'number') return numberFormatter.format(Number(value));
  if (col.type === 'date' && col.format) return dayjs(String(value)).format(toDayjsFormat(col.format));
  return String(value);
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

function humanizeFieldName(field: string): string {
  return field
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1).toLocaleLowerCase('tr-TR'))
    .join(' ');
}

function inferColumnType(value: unknown): ColumnConfig['type'] {
  if (value == null) return 'string';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'string';
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) return 'date';
  return 'string';
}

export function inferColumnsFromData(row: Record<string, unknown>): ColumnConfig[] {
  return Object.keys(row).map((field) => {
    const type = inferColumnType(row[field]);
    return {
      field,
      header: humanizeFieldName(field),
      type,
      format: type === 'date' ? 'dd.MM.yyyy' : null,
      agg: null,
      groupLevel: 0,
      width: null,
      pinned: false,
      filterable: true,
      searchable: true,
      cellRenderer: null,
    };
  });
}

function isNumericType(t: ColumnConfig['type']): boolean {
  return t === 'number' || t === 'currency' || t === 'percentage';
}

export function mapToDataGridColumns<R extends Record<string, unknown>>(
  source: ColumnConfig[],
): DataGridColumn<R>[] {
  return source.map((col) => {
    const numeric = isNumericType(col.type);
    const aggOk =
      col.agg === 'sum' || col.agg === 'avg' || col.agg === 'count' ||
      col.agg === 'min' || col.agg === 'max'
        ? col.agg
        : undefined;
    return {
      key: col.field,
      label: col.header,
      align: numeric ? 'right' : 'left',
      mono: numeric,
      sortable: true,
      hideable: true,
      groupable: !numeric,
      filter: numeric ? 'number' : 'text',
      agg: aggOk,
      width: col.width ?? undefined,
      sticky: col.pinned,
      render: (row: R) => {
        const v = row[col.field];
        if (v == null) return '';
        if (col.type === 'string' && typeof v !== 'object') return String(v);
        return formatValue(col, v);
      },
    };
  });
}
